// this is the actions list component
import { useEffect, useState } from 'react'
import { Button, Select, Flex } from 'antd'
import { ActionCard } from './ActionCard'
import { useFetchPotentialCleanPoints } from '../hooks/Issues'
import { useGetPlantConfig, useUserPlantInfo } from '../hooks/Plants'

const emptyAction = {
  assignee: null,
  description: null,
  type: null,
  start_date: null,
  due_date: null,
  close_date: null,
  status: 'open',
}

const getActions = (issue) => {
  if (!!issue && !!issue.actions) {
    return issue.actions
  }
  return [structuredClone(emptyAction)]
}

export const Actions = ({ issue, setIssue, readOnly, intl }) => {
  const [actions, setActions] = useState(getActions(issue))
  const [potentialCleanPoints, setPotentialCleanPoints] = useState([])
  const getPotentialCleanPoints = useFetchPotentialCleanPoints()

  // issue.vin_identifier is only set once an issue has been saved -- for a
  // brand new issue being drafted, fall back to the current session's plant
  // (which is what the issue will be saved with).
  const { plantInfo } = useUserPlantInfo()
  const vinIdentifier = issue?.vin_identifier ?? plantInfo?.vin_identifier
  const { config: plantConfig } = useGetPlantConfig({
    vin_identifier: vinIdentifier,
  })
  const showActionType = plantConfig?.show_action_type ?? true

  useEffect(() => {
    setActions(getActions(issue))
    if (issue?.id) {
      getPotentialCleanPoints.mutate(
        { issue_id: issue.id },
        {
          onSuccess: (res) => {
            setPotentialCleanPoints(
              res.map((vin) => {
                return { value: vin, label: vin }
              }),
            )
          },
        },
      )
    }
  }, [issue])

  const updateAction = (newAction, idx) => {
    const newActions = structuredClone(actions)
    if (newAction === null) {
      newActions.splice(idx, 1)
    } else {
      newActions[idx] = newAction
    }
    setActions(newActions)
    let newIssue = { ...issue, ...{ actions: newActions } }
    setIssue(newIssue)
  }

  const addNewAction = () => {
    const newActions = actions.concat([structuredClone(emptyAction)])
    setActions(newActions)
    let newIssue = { ...issue, ...{ actions: newActions } }
    setIssue(newIssue)
  }

  const statusOptions = [
    {
      label: intl.formatMessage({
        id: 'incidentMgmt.actions.optionOpen',
        defaultMessage: 'open',
      }),
      value: 'open',
    },
    {
      label: intl.formatMessage({
        id: 'incidentMgmt.actions.optionClosed',
        defaultMessage: 'closed',
      }),
      value: 'closed',
    },
  ]

  const updateIssueStatus = (value) => {
    setIssue({ ...issue, ...{ status: value } })
  }

  const updateIssueCleanPoint = (value) => {
    setIssue({ ...issue, ...{ clean_point: value } })
  }

  return (
    <Flex id="issue-actions" vertical={true} gap={10}>
      {intl.formatMessage({
        id: 'incidentMgmt.actions.issueStatus',
        defaultMessage: 'Issue Status',
      })}
      :{' '}
      <Select
        key="issue-status"
        className="status-input"
        value={issue.status || ''}
        onChange={(value) => updateIssueStatus(value)}
        disabled={readOnly}
        options={statusOptions}
      />
      {intl.formatMessage({
        id: 'incidentMgmt.actions.cleanPoint',
        defaultMessage: 'Clean Point',
      })}
      :{' '}
      <Select
        showSearch
        placeholder="Select VIN"
        optionFilterProp="label"
        key="issue-clean-point"
        className="status-input"
        value={issue.clean_point || ''}
        onChange={(value) => updateIssueCleanPoint(value)}
        disabled={readOnly}
        options={potentialCleanPoints}
      />
      {actions.map((action, idx) => {
        return (
          <ActionCard
            key={`action-card-${idx}`}
            action={action}
            idx={idx}
            updateAction={updateAction}
            readOnly={readOnly}
            intl={intl}
            issue={issue}
            showActionType={showActionType}
          />
        )
      })}
      {!readOnly && (
        <Button id="add-new-action-btn" onClick={addNewAction}>
          +{' '}
          {intl.formatMessage({
            id: 'incidentMgmt.actions.addAction',
            defaultMessage: 'Add Action',
          })}
        </Button>
      )}
    </Flex>
  )
}
